import { Autocomplete, FormControl, InputLabel, MenuItem, Paper, Select, TextField } from '@mui/material';
import { Col, Row } from 'antd';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ConversionFormDetails } from '../conversionType';
import { useEffect, useState } from 'react';
import { SearchableTable } from '../../../tables/dataIndexTable/AoisTable/SearchableTable';
import { EPSG_INdexData, compoundCRS, horizontalCRSList, verticalCRSList } from '../../../../common/EPSGIndexUtils';
import { GEOIDInfo } from '../../../../common/GeoidInfo';
import { DataIndexItem } from '../../../../types/interfaces';
import { CloseOutlined } from '@ant-design/icons';


interface ConversionDetailsProps {
    selectedConversionDetails: ConversionFormDetails;
    setSelectedConversionDetails: React.Dispatch<React.SetStateAction<ConversionFormDetails>>;
    data : DataIndexItem
}

enum CRS_TYPE {
    SOURCE_V = "source_v",
    TARGET_V = "target_v",
    SOURCE_H = "source_h",
    TARGET_H = "target_h"
}

const ReprojectionMenuDetails: React.FC<ConversionDetailsProps> = ({ selectedConversionDetails, setSelectedConversionDetails, data }) => {
    const [selectedCRSType, setSelectedCRSType] = useState<boolean | CRS_TYPE>(false);

    const [vTargetDisable, setVTargetDisable] = useState<boolean>(false);
    const [vSourceDisable, setVSourceDisable] = useState<boolean>(false);

    const activeProjection = window.activeProjection?.getCRS() || "";

    const [selectedTarget, setSelectedTarget] = useState<string>(activeProjection);
    const [selectedSource, setSelectedSource] = useState<string>(activeProjection);

    const params = data.params;

    const [targetH_CRS, setTargetH_CRS] = useState<EPSG_INdexData>({
        code: params?.reprojection?.out_srs?.split('+')[0] || ""
    } as EPSG_INdexData);
    const [targetV_CRS, setTargetV_CRS] = useState<EPSG_INdexData>({
        code: params?.reprojection?.out_srs?.split('+')[1] || ""
    } as EPSG_INdexData);

    const lasData = data.las_metadata;
    const [sourceH_CRS, setSourceH_CRS] = useState<EPSG_INdexData>({
        code: lasData?.epsg?.split('+')[0] || ""
    } as EPSG_INdexData);
    const [sourceV_CRS, setSourceV_CRS] = useState<EPSG_INdexData>({
        code: lasData?.epsg?.split('+')[1] || ""
    } as EPSG_INdexData);

    const [sourceGeoid, setSourceGeoid] = useState<string>(params?.reprojection?.in_geoid || "");
    const [targetGeoid, setTargetGeoid] = useState<string>(params?.reprojection?.out_geoid || "");

    useEffect(() => {
        setSelectedConversionDetails(prevState => {
            return {
                ...prevState,
                reprojection: {
                    ...prevState.reprojection,
                    in_srs: selectedSource,
                    out_srs: selectedTarget,
                    in_geoid: sourceGeoid,
                    out_geoid: targetGeoid
                }
            };
        })

    }, [selectedTarget, selectedSource, sourceGeoid, targetGeoid])

    useEffect(() => {
        let newCRS = targetH_CRS?.code;
        let kind = targetH_CRS?.kind;

        if (compoundCRS.includes(kind)) {
            setVTargetDisable(true)
        } else {
            setVTargetDisable(false)
        }
        if (targetV_CRS?.code !== "") {
            newCRS = `${newCRS}+${targetV_CRS.code}`;
        }
        setSelectedTarget(newCRS);
    }, [targetH_CRS, targetV_CRS])

    useEffect(() => {
        let newCRS = targetH_CRS?.code;
        let kind = targetH_CRS?.kind;

        if (!newCRS || compoundCRS.includes(kind)) {
            setVTargetDisable(true)
            setSelectedTarget(newCRS);
            setTargetV_CRS({ code: "" } as EPSG_INdexData);
            return;
        }
    }, [targetH_CRS])

    useEffect(() => {
        let newCRS = sourceH_CRS?.code;
        let kind = sourceH_CRS?.kind;

        if (compoundCRS.includes(kind)) {
            setVSourceDisable(true)
        } else {
            setVSourceDisable(false)
        }
        if (sourceV_CRS?.code !== "") {
            newCRS = `${newCRS}+${sourceV_CRS.code}`;
        }
        setSelectedSource(newCRS);
    }, [sourceH_CRS, sourceV_CRS])

    useEffect(() => {
        let newCRS = sourceH_CRS?.code;
        let kind = sourceH_CRS?.kind;
        if (!newCRS || compoundCRS.includes(kind)) {
            setVSourceDisable(true)
            setSelectedSource(newCRS);
            setSourceV_CRS({ code: "" } as EPSG_INdexData);
            return;
        }
    }, [sourceH_CRS])

    const handleChange = (field: string, value: string) => {
        if(field === 'in_geoid') {
            setSourceGeoid(value);
        } else {
            setTargetGeoid(value);
        }
    };

    const locked = data.locked;
    let classNameStr = 'field_toggle_icon'
    if (locked) {
        classNameStr += ' field_toggle_icon_disabled'
    }
    return (
        <>
            <div>
                <Row gutter={[20, 20]} className='panel_row panel_projection_main_row'>
                    <Col span={12} className='panel_projection_column panel_left_column'>
                        <div className='projection_header_title_box'>
                            <p>Source</p>
                        </div>
                        <Autocomplete
                            options={horizontalCRSList}
                            getOptionLabel={(option: EPSG_INdexData) => option.code}
                            value={
                                horizontalCRSList.find(
                                    (option: EPSG_INdexData) => option.code === (selectedConversionDetails.reprojection?.in_srs?.split('+')[0] || '').trim()
                                ) || null
                            }
                            onChange={(event, newValue) => {
                                if (!locked && newValue && typeof newValue !== 'string') {
                                    setSourceH_CRS(newValue as EPSG_INdexData);
                                }
                            }}
                            PaperComponent={({ children }) => (
                                <Paper className="customAutocompletePopper">{children}</Paper>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Horizontal CRS"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                <CloseOutlined className={classNameStr} onClick={locked ? undefined : () => { setSourceH_CRS({ code: "" } as EPSG_INdexData) }} />
                                                <MoreVertIcon className={classNameStr + " minus_12_right"} onClick={locked ? undefined : () => setSelectedCRSType(CRS_TYPE.SOURCE_H)} />
                                            </>
                                        ),
                                        readOnly: true,
                                    }}
                                />
                            )}
                        />

                        <Autocomplete
                            options={verticalCRSList}
                            getOptionLabel={(option: EPSG_INdexData) => option.code}
                            value={
                                verticalCRSList.find(
                                    (option: EPSG_INdexData) => option.code === (selectedConversionDetails?.reprojection.in_srs?.split('+')[1] || '').trim()
                                ) || null
                            }
                            onChange={(event, newValue) => {
                                if (!locked && newValue && typeof newValue !== 'string') {
                                    setSourceV_CRS(newValue as EPSG_INdexData);
                                }
                            }}
                            PaperComponent={({ children }) => (
                                <Paper className="customAutocompletePopper">{children}</Paper>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Vertical CRS"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                <CloseOutlined className={classNameStr} onClick={locked ? undefined : () => { setSourceV_CRS({ code: "" } as EPSG_INdexData) }} />
                                                <MoreVertIcon
                                                    className={
                                                        (classNameStr + " minus_12_right") + (vSourceDisable ? ' field_toggle_icon_disabled' : '')
                                                    }
                                                    onClick={locked || vSourceDisable ? undefined : () => setSelectedCRSType(CRS_TYPE.SOURCE_V)}
                                                />
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                        readOnly: true,
                                    }}
                                />
                            )}
                        />

                        <TextField
                            label="CRS"
                            disabled
                            value={selectedConversionDetails.reprojection.in_srs || ''}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel shrink={true}>Geoid</InputLabel>
                            <Select
                                labelId="Geoid"
                                label="Geoid"
                                value={selectedConversionDetails.reprojection.in_geoid || ''}
                                onChange={(e) => handleChange('in_geoid', e.target.value as string)}
                                fullWidth
                                IconComponent={KeyboardArrowDownIcon} // Use the custom dropdown icon
                                sx={{
                                    width: '100%',
                                    marginBottom: '10px',
                                    '& .MuiSelect-icon': { // Style the icon
                                        color: '#939598', // Change the color
                                        fontSize: '24px' // Change the size
                                    }
                                }}
                                disabled={locked}
                                MenuProps={{
                                    MenuListProps: {
                                        className: "customAutocompletePopper", // Apply custom styles to the menu list
                                    },
                                }}
                            >
                                {GEOIDInfo.map(item => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Col>
                    <Col span={12} className='panel_projection_column'>
                        <div className='projection_header_title_box'>
                            <p>Target</p>
                        </div>
                        <Autocomplete
                            options={horizontalCRSList}
                            getOptionLabel={(option: EPSG_INdexData) => option.code}
                            value={
                                horizontalCRSList.find(
                                    (option: EPSG_INdexData) => option.code === (selectedConversionDetails.reprojection?.out_srs?.split('+')[0] || '').trim()
                                ) || null
                            }
                            onChange={(event, newValue) => {
                                if (!locked && newValue && typeof newValue !== 'string') {
                                    setTargetH_CRS(newValue as EPSG_INdexData);
                                }
                            }}
                            PaperComponent={({ children }) => (
                                <Paper className="customAutocompletePopper">{children}</Paper>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Horizontal CRS"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                <CloseOutlined className={classNameStr} onClick={locked ? undefined : () => { setTargetH_CRS({ code: "" } as EPSG_INdexData) }} />
                                                <MoreVertIcon className={classNameStr + ' minus_12_right'} onClick={locked ? undefined : () => setSelectedCRSType(CRS_TYPE.TARGET_H)} />
                                            </>
                                        ),
                                        readOnly: true,
                                    }}
                                // className="autoComplete_dropdown"
                                />
                            )}
                        />

                        <Autocomplete
                            options={verticalCRSList}
                            getOptionLabel={(option: EPSG_INdexData) => option.code}
                            value={
                                verticalCRSList.find(
                                    (option: EPSG_INdexData) => option.code === (selectedConversionDetails?.reprojection.out_srs?.split('+')[1] || '').trim()
                                ) || null
                            }
                            onChange={(event, newValue) => {
                                if (!locked && newValue && typeof newValue !== 'string') {
                                    setTargetV_CRS(newValue as EPSG_INdexData);
                                }
                            }}
                            PaperComponent={({ children }) => (
                                <Paper className="customAutocompletePopper">{children}</Paper>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Vertical CRS"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                <CloseOutlined className={classNameStr} onClick={locked ? undefined : () => { setTargetV_CRS({ code: "" } as EPSG_INdexData) }} />
                                                <MoreVertIcon className={(classNameStr + ' minus_12_right') + (vTargetDisable ? ' field_toggle_icon_disabled' : '')}
                                                    onClick={locked || vTargetDisable ? undefined : () => setSelectedCRSType(CRS_TYPE.TARGET_V)}
                                                />
                                            </>
                                        ),
                                        readOnly: true,
                                    }}
                                />
                            )}
                        />

                        <TextField
                            label="CRS"
                            disabled
                            value={selectedConversionDetails.reprojection.out_srs || ''}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel shrink={true}>Geoid</InputLabel>
                            <Select
                                labelId="Geoid"
                                label="Geoid"
                                value={selectedConversionDetails.reprojection.out_geoid || ''}
                                onChange={(e) => handleChange('out_geoid', e.target.value as string)}
                                fullWidth
                                IconComponent={KeyboardArrowDownIcon} // Use the custom dropdown icon
                                sx={{
                                    width: '100%',
                                    marginBottom: '10px',
                                    '& .MuiSelect-icon': { // Style the icon
                                        color: '#939598', // Change the color
                                        fontSize: '24px' // Change the size
                                    }
                                }}
                                disabled={locked}
                                MenuProps={{
                                    MenuListProps: {
                                        className: "customAutocompletePopper", // Apply custom styles to the menu list
                                    },
                                }}
                            >
                                {GEOIDInfo.map(item => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Col>
                </Row>

            </div>

            {selectedCRSType && (
                <SearchableTable
                    onClose={() => setSelectedCRSType(false)}
                    title="Select Target EPSG"
                    titleColor="#fff"
                    titleFontSize="20px"
                    setSelectedRow={
                        selectedCRSType === CRS_TYPE.SOURCE_H ? setSourceH_CRS :
                            selectedCRSType === CRS_TYPE.TARGET_H ? setTargetH_CRS :
                                selectedCRSType === CRS_TYPE.SOURCE_V ? setSourceV_CRS :
                                    setTargetV_CRS
                    }
                    data={
                        selectedCRSType === CRS_TYPE.SOURCE_H || selectedCRSType === CRS_TYPE.TARGET_H ?
                            horizontalCRSList : verticalCRSList
                    }
                />
            )}
        </>
    );
}

export default ReprojectionMenuDetails;
